/**
 * Looping overworld bed — the groove from the Super Civica World intro,
 * ducked/paused whenever a video, battle, or cinematic owns the speakers.
 */
import { useEffect, useRef } from 'react'
import { useGame } from '@/context/GameContext'
import bedUrl from '@/media/overworld.m4a'

const TARGET_VOL = 0.28
const FADE_MS = 450

export default function OverworldMusic({ playing }: { playing: boolean }) {
  const { muteMusic } = useGame()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const fadeRef  = useRef<number | null>(null)

  useEffect(() => {
    const a = new Audio(bedUrl)
    a.loop = true
    a.preload = 'auto'
    a.volume = 0
    audioRef.current = a
    return () => {
      if (fadeRef.current) cancelAnimationFrame(fadeRef.current)
      a.pause()
      a.src = ''
      audioRef.current = null
    }
  }, [])

  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    const want = playing && !muteMusic

    const fadeTo = (target: number, thenPause: boolean) => {
      if (fadeRef.current) cancelAnimationFrame(fadeRef.current)
      const start = a.volume
      const t0 = performance.now()
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / FADE_MS)
        a.volume = start + (target - start) * t
        if (t < 1) {
          fadeRef.current = requestAnimationFrame(step)
        } else if (thenPause) {
          a.pause()
        }
      }
      fadeRef.current = requestAnimationFrame(step)
    }

    if (want) {
      a.play().then(() => fadeTo(TARGET_VOL, false)).catch(() => {/* autoplay blocked */})
    } else if (!a.paused) {
      fadeTo(0, true)
    }
  }, [playing, muteMusic])

  useEffect(() => {
    const onHide = () => { audioRef.current?.pause() }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [])

  return null
}
